<template>
  <div class="remarket-cont-info-page">
    <div class="page-name">
      <div @click="handleClose" class="pointer back-button">
        <i class="ace-icon fa fa-arrow-left" aria-hidden="true"></i>&nbsp;
        <q-tooltip>Обратно на список подрядчиков</q-tooltip>
      </div>
      <span>Информация о подрядчике в Remarket</span>
    </div>
    <div class="contractor-info">
      <div class="contractor-header">
        <div class="logo-container" @click="handleLogoClick">
          <CustomImg
            :src="previewLogo || `${FILE_URL_OFFICE}${contractorData.logo_url}`"
            alt="contractor logo"
            class="contractor-logo"
            :isStaticImg="true"
            :defaultSrc="placeholder"
            :class="{ 'has-logo': contractorData.logo_url || previewLogo }"
          />
          <div class="edit-overlay">
            <q-icon name="edit" size="24px" />
          </div>
          <q-btn
            class="upload-logo-btn"
            label="Загрузить логотип"
            color="primary"
            @click="handleLogoClick"
            v-if="$q.screen.lt.sm"
          />
        </div>
        <div class="info-container">
          <h2 class="contractor-name">{{ contractorData.contractor_name }}</h2>
          <div class="description-container" @click="isDescriptionEdit = true">
            <div v-if="!isDescriptionEdit" class="description-text">
              {{ formData.profile || "Описание отсутствует" }}
            </div>
            <q-input
              v-else
              v-model="formData.profile"
              type="textarea"
              outlined
              autogrow
              style="word-wrap: break-word; white-space: pre-wrap"
            />
          </div>
        </div>
      </div>

      <div class="form-actions" v-if="hasChanges">
        <q-btn
          label="Отмена"
          color="grey"
          @click="handleCancelChanges"
          class="cancel-btn"
        />
        <q-btn
          label="Сохранить изменения"
          color="secondary"
          @click="handleSaveChanges"
        />
      </div>
    </div>

    <CustomTable
      title="Фотографии выполненных работ"
      :rowsData="photosData"
      :columns="photoColumns"
      :loading="loading"
      :custom-body="true"
      :sticky-header="true"
      style="margin-bottom: 50px"
    >
      <template #top-right>
        <div class="header-actions">
          <q-btn
            label="Добавить фото"
            color="primary"
            @click="this.showAddPhotoModal"
            class="action-btn"
            icon="add"
          />
        </div>
      </template>
      <template #customBody="props">
        <q-tr>
          <custom-table-cell align="center">
            <Clickable @click="() => showAddReviewModal(props.row)">
              {{ props.row.partner_project_photo_id }}
            </Clickable>
          </custom-table-cell>
          <custom-table-cell align="center">
            <a
              :href="`${FILE_URL_OFFICE}${props.row.photo_url}`"
              target="_blank"
            >
              <CustomImg
                :src="props.row.photo_url_mini || props.row.photo_url"
                style="height: 100px"
                alt="contractor_photo"
              />
            </a>
          </custom-table-cell>
          <custom-table-cell align="center">
            <CustomIcon
              icon="trash"
              :approve-mode="true"
              :loading="deleteLoading[props.row.partner_project_photo_id]"
              :approve="
                () => handleDeletePhoto(props.row.partner_project_photo_id)
              "
            />
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>

    <CustomTable
      title="Отзывы"
      :rowsData="reviewsData"
      :columns="reviewsColumns"
      :loading="loading"
      :custom-body="true"
      :sticky-header="true"
    >
      <template #top-right>
        <div class="header-actions">
          <q-btn
            label="Добавить отзыв"
            color="primary"
            @click="this.showAddReviewModal"
            class="action-btn"
            icon="add"
          />
        </div>
      </template>
      <template #customBody="props">
        <q-tr>
          <custom-table-cell align="center">
            {{ props.row.partner_review_id }}
          </custom-table-cell>
          <custom-table-cell align="center">
            {{ props.row.client_name }}
          </custom-table-cell>
          <custom-table-cell align="center">
            {{ props.row.review_text }}
          </custom-table-cell>
          <custom-table-cell align="center">
            {{ props.row.review_score }}
          </custom-table-cell>
          <custom-table-cell align="center">
            <CustomIcon
              icon="trash"
              :approve-mode="true"
              :loading="deleteLoading[props.row.partner_review_id]"
              :approve="() => handleDeleteReview(props.row.partner_review_id)"
            />
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>
  </div>
</template>

<script>
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import CustomIcon from "@/components/common/CustomIcon";
import CustomImg from "@/components/common/CustomImg";
import CustomUploader from "@/components/form/CustomUploader";
import Clickable from "@/components/common/Clickable";
import placeholder from "@/assets/img/placeholder.png";

import { FILE_URL_OFFICE } from "@/constants";
import { appActions } from "@/services/store/modules/app_module/app_actions";
import { MODAL_NAMES } from "@/constants";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import {
  getContractorInfo,
  updateContractorInfo,
  getContractorPhotos,
  deleteContractorPhoto,
  photoColumns,
  addContractorPhoto,
  reviewsColumns,
  getContractorReviews,
  addContractorReview,
  deleteContractorReview,
} from "@/views/RemarketContractorInfo/services";

export default {
  name: "RemarketContractorInfo",
  components: {
    CustomTable,
    CustomTableCell,
    CustomIcon,
    CustomImg,
    CustomUploader,
    Clickable,
  },
  data() {
    return {
      placeholder,      
      FILE_URL_OFFICE,
      contractorId: this.$route.query.contractorId,
      loading: false,
      deleteLoading: {},
      contractorData: {},
      formData: {
        logo: [],
        profile: "",
      },
      photosData: [],
      photoColumns,
      reviewsData: [],
      reviewsColumns,
      isLogoEdit: false,
      isDescriptionEdit: false,
      originalDescription: "",
      previewLogo: null,
      originalLogo: null,
    };
  },
  computed: {
    hasChanges() {
      return this.isLogoEdit || this.isDescriptionEdit;
    },
  },
  methods: {
    handleClose() {
      this.$router.push("handbook/handbook-contractors");
    },
    async getData() {
      this.loading = true;
      const [contractorInfo, contractorPhotos, contractorReviews] =
        await Promise.all([
          getContractorInfo(this.contractorId),
          getContractorPhotos(this.contractorId),
          getContractorReviews(this.contractorId),
        ]);
      this.loading = false;
      if (contractorInfo) {
        this.contractorData = contractorInfo;
        this.originalDescription = contractorInfo.profile;
        this.formData.profile = contractorInfo.profile;
      }
      if (contractorPhotos) {
        this.photosData = contractorPhotos;
      }
      if (contractorReviews) {
        this.reviewsData = contractorReviews;
      }
    },
    async handleDeletePhoto(photoId) {
      this.deleteLoading = { ...this.deleteLoading, [photoId]: true };
      const res = await deleteContractorPhoto(photoId);
      this.deleteLoading = { ...this.deleteLoading, [photoId]: false };
      if (res) {
        this.photosData = this.photosData.filter(
          (photo) => photo.partner_project_photo_id !== photoId
        );
        this.showSucceedSnackbar("Фото успешно удалено");
      }
    },
    showAddPhotoModal() {
      this.$store.commit(
        appActions.showModal(MODAL_NAMES.ADD_CONTRACTOR_WORKS_PHOTO, {
          onSubmit: (body) => this.handlePhotoAdd(body),
          title: "Добавить фото",
          contractor_id: this.contractorId,
        })
      );
    },
    async handlePhotoAdd(body) {
      const res = await addContractorPhoto(body);
      if (!res) return;
      this.photosData = res;
      this.showSucceedSnackbar("Фото успешно добавлено");
      this.$store.commit(appActions.closeModal());
    },
    onChange(key, value) {
      this.formData[key] = value;
    },
    handleLogoClick() {
      this.isLogoEdit = true;
      this.isDescriptionEdit = true;
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = (e) => {
        const file = e.target.files[0];
        if (file) {
          this.convertToBase64(file).then((base64) => {
            this.previewLogo = base64;
            this.formData.logo = [file];
          });
        }
      };
      input.click();
    },
    convertToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    handleCancelChanges() {
      this.isLogoEdit = false;
      this.isDescriptionEdit = false;
      this.formData.profile = this.originalDescription;
      this.previewLogo = null;
      this.formData.logo = [];
    },
    async handleSaveChanges() {
      if (this.formData.logo) {
        this.formData.logo = this.formData.logo[0];
      }
      const payload = {
        contractor_id: this.contractorId,
        ...this.formData,
      };
      const res = await updateContractorInfo(payload);
      this.isLogoEdit = false;
      this.isDescriptionEdit = false;
      this.formData.logo = [];
      this.previewLogo = null;
      if (!res) return;
      this.contractorData = res;
      this.originalDescription = res.profile;
      this.formData.profile = res.profile;
      this.showSucceedSnackbar("Информация успешно обновлена");
    },
    showAddReviewModal(row) {
      const body = {
        contractor_id: this.contractorId,
        partner_review_id: row.partner_review_id,
        client_name: row.client_name,
        review_text: row.review_text,
        review_score: row.review_score,
      };
      this.$store.commit(
        appActions.showModal(MODAL_NAMES.ADD_CONTRACTOR_REVIEW, {
          onSubmit: (body) => this.handleReviewAdd(body),
          title: "Добавить отзыв",
          body: body,
        })
      );
    },
    async handleReviewAdd(body) {
      const res = await addContractorReview(body);
      if (!res) return;
      this.reviewsData = res;
      this.showSucceedSnackbar("Отзыв успешно добавлен");
      this.$store.commit(appActions.closeModal());
    },
    async handleDeleteReview(reviewId) {
      this.deleteLoading = { ...this.deleteLoading, [reviewId]: true };
      const res = await deleteContractorReview(reviewId);
      this.deleteLoading = { ...this.deleteLoading, [reviewId]: false };
      if (res) {
        this.reviewsData = this.reviewsData.filter(
          (review) => review.partner_review_id !== reviewId
        );
        this.showSucceedSnackbar("Отзыв успешно удален");
      }
    },
  },
  async mounted() {
    await this.getData();
  },
  setup() {
    const { showSucceedSnackbar } = useCustomSnackbar();
    return { showSucceedSnackbar };
  },
};
</script>

<style scoped lang="scss">
.remarket-cont-info-page {
  padding: 20px;
  .page-name {
    font-size: 24px;
    display: inline-block;
    vertical-align: middle;
    margin-bottom: 20px;
  }
  .back-button {
    font-size: 24px;
    z-index: 10;
    display: inline;
  }
  .pointer {
    cursor: pointer;
  }
  .pointer:hover {
    color: red;
  }
}

.contractor-info {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.contractor-header {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.logo-container {
  position: relative;
  width: 150px;
  height: 150px;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;

  &:hover .edit-overlay {
    opacity: 1;
  }
}

.contractor-logo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  max-width: none !important;

  &.has-logo {
    max-width: none !important;
    filter: none !important;
    opacity: 1 !important;
  }
}

.edit-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  color: white;
}

.info-container {
  flex: 1;
}

.contractor-name {
  margin: 0 0 10px 0;
  font-size: 24px;
}

.description-container {
  max-width: 100%;
  cursor: pointer;
  padding: 10px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  white-space: pre-wrap; // сохраняет переносы и переносит длинные строки
  word-wrap: break-word; // переносит длинные слова
  overflow-wrap: break-word;
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
}

.description-text {
  white-space: pre-wrap; // сохраняет переносы и переносит длинные строки
  word-wrap: break-word; // переносит длинные слова
  overflow-wrap: break-word;
  line-height: 1.5;
}

.logo-uploader {
  margin-top: 10px;
  max-width: 300px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.cancel-btn {
  margin-right: 10px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h2 {
    margin: 0;
  }
}

.header-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  margin-left: 10px;
}
</style>
